import { useState, ReactNode } from "react";

export type UploadedFile = {
  /** Base64 encoded file data */
  file: string;
  /** Original filename */
  name: string;
  /** File size in bytes */
  size: number;
  /** MIME type */
  type: string;
  /** Original File object */
  original: File;
};

export type Props = {
  /** Field name for identification */
  name: string;
  /** Unique hash for file uploader element ID */
  hash: string;
  /** Maximum file size in bytes */
  maxFileSize: number;
  /** Currently uploaded file value */
  value?: UploadedFile | null;
  /** Change handler - receives uploaded file data */
  onChange?: (value: UploadedFile | null) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Map of allowed MIME types */
  allowedFileTypes?: Record<string, boolean>;
  /** Human-readable accepted file types description */
  acceptedFileTypes?: string;
  /** Optional resize function for images */
  resize?: (file: File) => Promise<File>;
  /** Transform function to customize the stored value */
  transform?: (file: File, base64: string) => UploadedFile | Promise<UploadedFile>;
  /** Additional CSS classes */
  className?: string;
  /** Render the file uploader component */
  renderUploader: (props: {
    hash: string;
    onUpload: (file: File, onProgress: (progress: number) => void) => Promise<void>;
    isHidden: boolean;
    allowedFileTypes?: Record<string, boolean>;
    acceptedFileTypes?: string;
  }) => ReactNode;
  /** Render the preview section when file is uploaded */
  renderPreview?: (props: {
    value: UploadedFile;
    onChangeClick: () => void;
    isChanged: boolean;
    disabled?: boolean;
  }) => ReactNode;
  /** Render the error message */
  renderError?: (props: { error: string }) => ReactNode;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Converts a File to base64 string
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

/**
 * Base generic file upload component with no styling.
 * Handles file validation, conversion, and state management.
 * Use render props to customize the uploader, preview, and error display.
 *
 * @example
 * // In shared/ui/GenericUploadInput.tsx
 * import { GenericUploadInputBase, type Props as GenericUploadInputBaseProps } from "@core/features/uploadFile";
 *
 * export function GenericUploadInput(props: GenericUploadInputBaseProps) {
 *   return (
 *     <GenericUploadInputBase
 *       {...props}
 *       renderUploader={({ hash, onUpload, isHidden }) => (
 *         <FileUploader hash={hash} uploadFileHandler={onUpload} className={isHidden ? "hidden" : ""} />
 *       )}
 *       renderPreview={({ value, onChangeClick }) => (
 *         <div>
 *           <img src={value.file} alt={value.name} />
 *           <Button onClick={onChangeClick}>Change</Button>
 *         </div>
 *       )}
 *     />
 *   );
 * }
 */
export function GenericUploadInputBase({
  name,
  hash,
  maxFileSize,
  value = null,
  onChange,
  onBlur: _onBlur,
  error,
  disabled,
  allowedFileTypes,
  acceptedFileTypes,
  resize,
  transform,
  className,
  renderUploader,
  renderPreview,
  renderError,
  "data-testid": testId,
}: Props) {
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isChanged, setIsChanged] = useState<boolean>(false);

  const handleFileUpload = async (
    file: File,
    _onProgress: (progress: number) => void
  ) => {
    setErrorText(null);

    try {
      if (allowedFileTypes && !allowedFileTypes[file.type]) {
        setErrorText(`File type ${file.type} is not supported.`);
        return;
      }

      const resized = resize ? await resize(file) : file;
      if (resized.size > maxFileSize) {
        setErrorText("File too large.");
        return;
      }

      const base64 = await fileToBase64(resized);

      const finalValue: UploadedFile = transform
        ? await transform(resized, base64)
        : {
            file: base64,
            name: resized.name,
            size: resized.size,
            type: resized.type,
            original: resized,
          };

      onChange?.(finalValue);
      setIsChanged(true);
    } catch (e: any) {
      setErrorText(e.message || "Upload failed");
    }
  };

  const handleChangeClick = () => {
    document.getElementById(`file-upload${hash}`)?.click();
  };

  return (
    <div
      className={className}
      data-testid={testId || `upload-input-${name}`}
      data-error={error || undefined}
      data-disabled={disabled || undefined}
    >
      {value?.file && renderPreview && renderPreview({
        value,
        onChangeClick: handleChangeClick,
        isChanged,
        disabled,
      })}

      {errorText && renderError && renderError({ error: errorText })}

      {renderUploader({
        hash,
        onUpload: handleFileUpload,
        isHidden: !!value?.file,
        allowedFileTypes,
        acceptedFileTypes,
      })}
    </div>
  );
}

GenericUploadInputBase.displayName = "GenericUploadInputBase";

